import { RefObject, useEffect, useRef } from 'react';
import { AREngine } from '../utils/arEngine';
import { UseARCallbacksProps } from '../types/ar/callbacks';

export const useARCallbacks = ({
    callbacks,
    targetName,
    sceneRef,
    engine,
    enabled = true,
}: UseARCallbacksProps): void => {
    const callbacksRef = useRef(callbacks);
    callbacksRef.current = callbacks;

    useEffect(() => {
        if (!enabled || !sceneRef.current) return;

        const scene = sceneRef.current;

        if (engine === 'xr8') {
            if (!targetName) return;

            callbacksRef.current?.arReady?.();

            const onImageFound = (event: Event) => {
                const detail = (event as CustomEvent).detail as { name?: string } | undefined;
                if (detail?.name !== targetName) return;
                console.log('[AR/XR8] Target found:', detail.name);
                callbacksRef.current?.targetFound?.();
            };

            const onImageLost = (event: Event) => {
                const detail = (event as CustomEvent).detail as { name?: string } | undefined;
                if (detail?.name !== targetName) return;
                console.log('[AR/XR8] Target lost:', detail.name);
                callbacksRef.current?.targetLost?.();
            };

            scene.addEventListener('xrimagefound', onImageFound);
            scene.addEventListener('xrimagelost', onImageLost);

            return () => {
                scene.removeEventListener('xrimagefound', onImageFound);
                scene.removeEventListener('xrimagelost', onImageLost);
            };
        }

        const imageTarget = scene.querySelector('#image_target');

        const onArReady = () => {
            console.log('[AR/MindAR] Scene is ready');
            callbacksRef.current?.arReady?.();
        };

        const onArError = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            console.error('[AR/MindAR] Error:', detail);
            callbacksRef.current?.arError?.(detail);
        };

        const onTargetFound = () => {
            console.log('[AR/MindAR] Target found');
            callbacksRef.current?.targetFound?.();
        };

        const onTargetLost = () => {
            console.log('[AR/MindAR] Target lost');
            callbacksRef.current?.targetLost?.();
        };

        scene.addEventListener('arReady', onArReady);
        scene.addEventListener('arError', onArError as EventListener);

        if (imageTarget) {
            imageTarget.addEventListener('targetFound', onTargetFound);
            imageTarget.addEventListener('targetLost', onTargetLost);
        }

        return () => {
            scene.removeEventListener('arReady', onArReady);
            scene.removeEventListener('arError', onArError as EventListener);
            if (imageTarget) {
                imageTarget.removeEventListener('targetFound', onTargetFound);
                imageTarget.removeEventListener('targetLost', onTargetLost);
            }
        };
    }, [enabled, engine, targetName, sceneRef]);
};
